/* eslint-disable eqeqeq */
import {
  AutoComplete,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Radio,
  Select,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import { Body } from "./MainScreen";
import moment from "moment";
import IAgrotekConfig from "../IAgrotekConfig";
import { GetOrderDetails, UpdateOrder } from "../services/OrderService";
import { getCustomerList } from "../services/ConfigService";
import { useHistory, useLocation } from "react-router-dom";
import {
  ArmStock,
  ChassisStock,
  GetArmStocks,
  GetChassisStocks,
  GetTankStocks,
  TankStock,
} from "../services/StockService";

const { Option, OptGroup } = Select;
const QRCode = require("qrcode.react");
type ConfigItem = { type: string; text: string };
const ORDER_STOCK_LOCATION = "saruhanli";

function EditOrderScreen(props: any) {
  const configFile: IAgrotekConfig = JSON.parse(
    localStorage.getItem("config") ?? "{}"
  );
  const [qrCodeValue, setQrCodeValue] = useState("");
  const [customerList, setCustomerList] = useState([] as any);
  const [orderDetails, setOrderDetails] = useState([] as any);
  const [productionUnit, setProductionUnit] = useState("turbo");
  const [payment_type, setPaymentType] = useState("normal");
  const [chassisStocks, setChassisStocks] = useState<ChassisStock[]>([]);
  const [armStocks, setArmStocks] = useState<ArmStock[]>([]);
  const [tankStocks, setTankStocks] = useState<TankStock[]>([]);
  const [selectedChassisStock, setSelectedChassisStock] =
    useState<ChassisStock | null>(null);
  const [selectedArmStock, setSelectedArmStock] = useState<ArmStock | null>(
    null
  );
  const [selectedTankStock, setSelectedTankStock] = useState<TankStock | null>(
    null
  );
  const [stockSelectRenderKey, setStockSelectRenderKey] = useState(0);
  const [temporaryCaldronColors, setTemporaryCaldronColors] = useState<
    ConfigItem[]
  >([]);
  const [serialState, setSerialState] = useState({
    brand: "",
    type: "",
    cooler_type: "",
    arm_type: "",
    tank_size: 0,
    pump: "",
    order_no: "",
    order_amount: 0,
    year: "",
    proforma_no: "",
  });

  const [isLoading, setIsLoading] = useState(false);

  let serialCode = SerialCodeGenerator();
  const [form] = Form.useForm();

  const { pathname }: any = useLocation();
  const history = useHistory();
  const serialNumber = pathname.split("/")[2];

  const configuredCaldronColors: ConfigItem[] = useMemo(
    () =>
      configFile?.caldron_color?.length > 0
        ? configFile.caldron_color
        : configFile?.chassis_color || [],
    [configFile]
  );

  const selectedStockCount = useMemo(
    () =>
      [selectedChassisStock, selectedArmStock, selectedTankStock].filter(
        Boolean
      ).length,
    [selectedChassisStock, selectedArmStock, selectedTankStock]
  );

  const filteredChassisStocks = useMemo(
    () =>
      chassisStocks.filter(
        (item) =>
          item.production_unit === productionUnit &&
          item.location === ORDER_STOCK_LOCATION &&
          Number(item.quantity) > 0
      ),
    [chassisStocks, productionUnit]
  );

  const filteredArmStocks = useMemo(
    () =>
      armStocks.filter(
        (item) =>
          item.location === ORDER_STOCK_LOCATION && Number(item.quantity) > 0
      ),
    [armStocks]
  );

  const filteredTankStocks = useMemo(
    () =>
      tankStocks.filter(
        (item) =>
          item.location === ORDER_STOCK_LOCATION && Number(item.quantity) > 0
      ),
    [tankStocks]
  );

  const lockedFields = useMemo(() => {
    const lockMap: Record<string, boolean> = {};

    if (selectedChassisStock) {
      lockMap.type = true;
      lockMap.brand = true;
      lockMap.tank_size = true;
      if (selectedChassisStock.production_unit === "turbo") {
        lockMap.cooler_type = true;
      }
      if (selectedChassisStock.production_unit === "kollu") {
        lockMap.arm_type = true;
      }
    }

    if (selectedArmStock) {
      lockMap.brand = true;
      lockMap.arm_type = true;
      lockMap.arm_length = true;
      lockMap.meme_distance = true;
      lockMap.meme_type = true;
    }

    if (selectedTankStock) {
      lockMap.brand = true;
      lockMap.caldron_color = true;
      lockMap.tank_size = true;
      lockMap.caldron_type = true;
    }

    return lockMap;
  }, [selectedChassisStock, selectedArmStock, selectedTankStock]);

  const caldronColorOptions = useMemo(() => {
    const optionsByType = new Map<string, ConfigItem>();
    [...configuredCaldronColors, ...temporaryCaldronColors].forEach((item) => {
      if (item?.type) optionsByType.set(String(item.type), item);
    });
    const existingOrderColor = orderDetails?.orderDetails?.caldron_color;
    if (existingOrderColor && !optionsByType.has(String(existingOrderColor))) {
      optionsByType.set(String(existingOrderColor), {
        type: String(existingOrderColor),
        text: String(existingOrderColor),
      });
    }
    return Array.from(optionsByType.values());
  }, [configuredCaldronColors, orderDetails, temporaryCaldronColors]);

  const ensureTemporaryCaldronColor = (value?: string | null) => {
    const colorValue = String(value ?? "").trim();
    if (!colorValue) return;

    const existsInOptions = caldronColorOptions.some(
      (item) => String(item.type) === colorValue
    );
    if (existsInOptions) return;

    setTemporaryCaldronColors((current) => [
      ...current,
      { type: colorValue, text: colorValue },
    ]);
  };

  const getShortend = (items: any[] = [], value: any) => {
    const found = items.find((item: any) => String(item.type) === String(value));
    return found?.shortend ?? "";
  };

  const resetStockSelectControl = () => {
    setStockSelectRenderKey((current) => current + 1);
  };

  const clearStockControlledFields = (
    fieldsToClear: string[],
    nextChassisStock: ChassisStock | null,
    nextArmStock: ArmStock | null,
    nextTankStock: TankStock | null
  ) => {
    const nextValues: Record<string, any> = {};

    fieldsToClear.forEach((field) => {
      nextValues[field] = undefined;
    });

    if (nextChassisStock) {
      const chassisValues = {
        type: nextChassisStock.type,
        brand: nextChassisStock.brand,
        tank_size: String(nextChassisStock.tank_size),
        ...(nextChassisStock.production_unit === "turbo"
          ? { cooler_type: nextChassisStock.fan_type }
          : { arm_type: nextChassisStock.arm_type }),
      };
      fieldsToClear.forEach((field) => {
        if (field in chassisValues)
          nextValues[field] =
            chassisValues[field as keyof typeof chassisValues];
      });
    }

    if (nextArmStock) {
      const armValues = {
        brand: nextArmStock.brand,
        arm_type: nextArmStock.arm_type,
        arm_length: nextArmStock.arm_length,
        meme_distance: nextArmStock.meme_distance,
        meme_type: nextArmStock.meme_type,
      };
      fieldsToClear.forEach((field) => {
        if (field in armValues)
          nextValues[field] = armValues[field as keyof typeof armValues];
      });
    }

    if (nextTankStock) {
      const tankValues = {
        brand: nextTankStock.brand,
        caldron_color: nextTankStock.caldron_color,
        tank_size: String(nextTankStock.tank_size),
        caldron_type: nextTankStock.caldron_type,
      };
      fieldsToClear.forEach((field) => {
        if (field in tankValues)
          nextValues[field] = tankValues[field as keyof typeof tankValues];
      });
    }

    form.setFieldsValue(nextValues);
    setSerialState((prev) => {
      const nextSerialState = { ...prev };

      if (fieldsToClear.includes("brand")) {
        nextSerialState.brand = nextValues.brand
          ? getShortend(configFile.brand, nextValues.brand)
          : "";
      }
      if (fieldsToClear.includes("type")) {
        nextSerialState.type = nextValues.type
          ? getShortend(configFile.type, nextValues.type)
          : "";
      }
      if (fieldsToClear.includes("tank_size")) {
        nextSerialState.tank_size = nextValues.tank_size
          ? parseInt(getShortend(configFile.tank_size, nextValues.tank_size)) ||
            0
          : 0;
      }
      if (fieldsToClear.includes("arm_type")) {
        nextSerialState.arm_type = nextValues.arm_type
          ? getShortend(configFile.arm_type, nextValues.arm_type)
          : "";
      }

      return nextSerialState;
    });
  };

  const hasCompatibilityConflict = (values: Record<string, any>) => {
    if (selectedStockCount === 0) return false;

    for (const [key, value] of Object.entries(values)) {
      if (value === undefined || value === null || value === "") continue;
      const currentValue = form.getFieldValue(key);
      if (
        currentValue !== undefined &&
        currentValue !== null &&
        currentValue !== "" &&
        String(currentValue) !== String(value)
      ) {
        Modal.error({
          title: "Uyumsuz Stok",
          content:
            "Seçtiğiniz stok, formdaki mevcut stok seçimi ile uyumlu değil.",
          okText: "Tamam",
        });
        resetStockSelectControl();
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    if (productionUnit === "diger") {
      setSelectedChassisStock(null);
      setSelectedArmStock(null);
      setSelectedTankStock(null);
      return;
    }

    if (
      selectedChassisStock &&
      selectedChassisStock.production_unit !== productionUnit
    ) {
      setSelectedChassisStock(null);
    }

    if (productionUnit !== "kollu" && selectedArmStock) {
      setSelectedArmStock(null);
    }
  }, [productionUnit, selectedChassisStock, selectedArmStock]);

  const handleChassisStockChange = (stockId?: number) => {
    if (!stockId) {
      setSelectedChassisStock(null);
      clearStockControlledFields(
        ["type", "brand", "tank_size", "cooler_type", "arm_type"],
        null,
        selectedArmStock,
        selectedTankStock
      );
      return;
    }

    const selected = filteredChassisStocks.find((item) => item.id === stockId);
    if (!selected) return;

    const conflictValues: Record<string, any> = {
      type: selected.type,
      brand: selected.brand,
      tank_size: selected.tank_size,
    };
    if (selected.production_unit === "turbo") {
      conflictValues.cooler_type = selected.fan_type;
    }
    if (selected.production_unit === "kollu") {
      conflictValues.arm_type = selected.arm_type;
    }

    if (
      selectedStockCount > (selectedChassisStock ? 1 : 0) &&
      hasCompatibilityConflict(conflictValues)
    ) {
      return;
    }

    setSelectedChassisStock(selected);
    setProductionUnit(selected.production_unit);
    form.setFieldsValue({
      production_unit: selected.production_unit,
      type: selected.type,
      brand: selected.brand,
      tank_size: String(selected.tank_size),
      ...(selected.production_unit === "turbo"
        ? { cooler_type: selected.fan_type }
        : { arm_type: selected.arm_type }),
    });

    setSerialState((prev) => ({
      ...prev,
      type: getShortend(configFile.type, selected.type),
      brand: getShortend(configFile.brand, selected.brand),
      tank_size:
        parseInt(getShortend(configFile.tank_size, selected.tank_size)) ||
        prev.tank_size,
      cooler_type:
        selected.production_unit === "turbo"
          ? getShortend(configFile.cooler_type, selected.fan_type)
          : prev.cooler_type,
      arm_type:
        selected.production_unit === "kollu"
          ? getShortend(configFile.arm_type, selected.arm_type)
          : prev.arm_type,
    }));
  };

  const handleArmStockChange = (stockId?: number) => {
    if (!stockId) {
      setSelectedArmStock(null);
      clearStockControlledFields(
        ["brand", "arm_type", "arm_length", "meme_distance", "meme_type"],
        selectedChassisStock,
        null,
        selectedTankStock
      );
      return;
    }

    const selected = filteredArmStocks.find((item) => item.id === stockId);
    if (!selected) return;

    if (
      selectedStockCount > (selectedArmStock ? 1 : 0) &&
      hasCompatibilityConflict({
        brand: selected.brand,
        arm_type: selected.arm_type,
      })
    ) {
      return;
    }

    setSelectedArmStock(selected);
    form.setFieldsValue({
      brand: selected.brand,
      arm_type: selected.arm_type,
      arm_length: selected.arm_length,
      meme_distance: selected.meme_distance,
      meme_type: selected.meme_type,
    });
    setSerialState((prev) => ({
      ...prev,
      brand: getShortend(configFile.brand, selected.brand),
      arm_type: getShortend(configFile.arm_type, selected.arm_type),
    }));
  };

  const handleTankStockChange = (stockId?: number) => {
    if (!stockId) {
      setSelectedTankStock(null);
      clearStockControlledFields(
        ["brand", "caldron_color", "tank_size", "caldron_type"],
        selectedChassisStock,
        selectedArmStock,
        null
      );
      return;
    }

    const selected = filteredTankStocks.find((item) => item.id === stockId);
    if (!selected) return;
    ensureTemporaryCaldronColor(selected.caldron_color);

    if (
      selectedStockCount > (selectedTankStock ? 1 : 0) &&
      hasCompatibilityConflict({
        brand: selected.brand,
        tank_size: selected.tank_size,
      })
    ) {
      return;
    }

    setSelectedTankStock(selected);
    form.setFieldsValue({
      brand: selected.brand,
      caldron_color: selected.caldron_color,
      caldron_stock: "onstock",
      tank_size: String(selected.tank_size),
      caldron_type: selected.caldron_type,
    });
    setSerialState((prev) => ({
      ...prev,
      brand: getShortend(configFile.brand, selected.brand),
      tank_size:
        parseInt(getShortend(configFile.tank_size, selected.tank_size)) ||
        prev.tank_size,
    }));
  };

  useEffect(() => {
    GetOrderDetails(serialNumber).then((orderDetails) => {
      const brand = configFile.brand.find(
        (brand) => brand.type == orderDetails.brand
      );

      const type = configFile.type.find(
        (type) => type.type == orderDetails.type
      );

      const cooler_type = configFile.cooler_type.find(
        (type) => type.type == orderDetails.cooler_type
      );

      const arm_type = configFile.arm_type.find(
        (type) => type.type == orderDetails.arm_type
      );

      const tank_size = configFile.tank_size.find(
        (size) => size.type == orderDetails.tank_size
      );

      const pump = configFile.pump.find(
        (pump) => pump.type == orderDetails.pump
      );
      setSerialState({
        brand: brand?.shortend || "",
        type: type?.shortend || "",
        cooler_type: cooler_type?.shortend || "",
        arm_type: arm_type?.shortend || "",
        tank_size: Number(tank_size?.shortend) || 0,
        pump: pump?.shortend || "",
        order_no: orderDetails.order_no,
        order_amount: orderDetails.order_amount,
        year: orderDetails.year,
        proforma_no: orderDetails.proforma_no,
      });

      delete orderDetails.date;
      delete orderDetails.year;
      setProductionUnit(orderDetails.production_unit);
      setPaymentType(orderDetails.payment_type);

      setOrderDetails({
        orderDetails,
        caldron_stock: "onstock",
        order_amount: String(orderDetails.order_amount),
      });
      form.setFieldsValue({
        ...orderDetails,
        caldron_stock: "onstock",
        order_amount: String(orderDetails.order_amount),
      });
    });
  }, [serialNumber]);

  useEffect(() => {
    getCustomerList().then((res) => {
      setCustomerList(res);
    });
    Promise.all([GetChassisStocks(), GetArmStocks(), GetTankStocks()]).then(
      ([chassis, arms, tanks]) => {
        setChassisStocks(chassis);
        setArmStocks(arms);
        setTankStocks(tanks);
      }
    );
  }, []);

  return (
    <Body
      style={{
        display: "flex",
        justifyContent: "space-evenly",
        flexWrap: "wrap-reverse",
        overflow: "auto",
      }}
    >
      <Form
        form={form}
        initialValues={orderDetails?.orderDetails}
        onFieldsChange={() => {
          let product =
            form.getFieldValue("brand") == "turktraktor"
              ? form.getFieldValue("brand") + " " + form.getFieldValue("model")
              : form.getFieldValue("production_unit") == "turbo"
              ? form.getFieldValue("brand") +
                " " +
                form.getFieldValue("tank_size") +
                "LT " +
                form.getFieldValue("cooler_type") +
                form.getFieldValue("fan_radius") +
                "cm " +
                form.getFieldValue("fan_type")
              : form.getFieldValue("production_unit") == "kollu"
              ? form.getFieldValue("brand") +
                " " +
                form.getFieldValue("tank_size") +
                "LT " +
                form.getFieldValue("arm_type") +
                " " +
                form.getFieldValue("arm_length")
              : form.getFieldValue("production_unit") == "diger"
              ? form.getFieldValue("brand") + " " + form.getFieldValue("type")
              : "";
          setQrCodeValue(serialCode + "," + product.toUpperCase());
          if (
            form.getFieldValue("model") &&
            form.getFieldValue("brand") == "turktraktor"
          ) {
            const model = form.getFieldValue("model");
            ensureTemporaryCaldronColor(model);
            if (form.getFieldValue("caldron_color") !== model) {
              form.setFieldsValue({ caldron_color: model });
            }
          }
        }}
        onFinish={async (values) => {
          setIsLoading(true);
          values.selected_chassis_stock_id =
            productionUnit !== "diger" && selectedChassisStock
              ? selectedChassisStock.id
              : null;
          values.selected_arm_stock_id =
            productionUnit !== "diger" && selectedArmStock
              ? selectedArmStock.id
              : null;
          values.selected_tank_stock_id =
            productionUnit !== "diger" && selectedTankStock
              ? selectedTankStock.id
              : null;
          let address =
            values.address + " " + values.city + " " + values.country;
          values.address = address;
          delete values.city;
          delete values.country;
          const result = await UpdateOrder(values, serialCode);
          setIsLoading(false);
          if (result) {
            history.goBack();
          }
          // if (result === true) {
          //   printQRCode(values.order_amount);
          //   form.resetFields();
          // }
        }}
        labelCol={{ span: 5 }}
        wrapperCol={{ span: 24 }}
        style={{ maxWidth: "calc(100% - 168px)", minWidth: "450px" }}
        requiredMark={false}
      >
        <Form.Item name="date" initialValue={moment()} label="Tarih" required>
          <DatePicker disabled />
        </Form.Item>

        <Form.Item
          name="customer"
          label="Müşteri Adı"
          rules={[
            { required: true, message: "Bu alan boş bırakılamaz" },
            { max: 250, message: "Maksimum sınır aşıldı." },
          ]}
          required
        >
          <AutoComplete
            style={{ width: 200 }}
            options={customerList}
            placeholder="aramak için yazın."
            filterOption={(inputValue, option) =>
              option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !==
              -1
            }
          />
        </Form.Item>
        <Form.Item
          name="tckn"
          label="TCKN"
          rules={[{ max: 11, message: "Maksimum sınır aşıldı." }]}
          required
        >
          <Input maxLength={11} />
        </Form.Item>
        <Form.Item
          name="address"
          label="Adresi"
          rules={[
            { required: true, message: "Bu alan boş bırakılamaz" },
            { max: 250, message: "Maksimum sınır aşıldı." },
          ]}
          required
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="phone_number"
          label="Telefon No"
          rules={[
            { required: true, message: "Bu alan boş bırakılamaz" },
            {
              max: 10,
              min: 10,
              message: "Telefon numarası 10 haneden oluşmalıdır.",
            },
          ]}
          required
        >
          <Input addonBefore="+90" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          name="production_unit"
          label="Üretim Birimi"
          initialValue={orderDetails?.orderDetails?.productUnit}
          required
        >
          <Radio.Group
            onChange={(e) => {
              setProductionUnit(e.target.value);
            }}
          >
            <Radio value="turbo">Turbo</Radio>
            <Radio value="kollu">Kollu</Radio>
            <Radio value="diger">Diğer</Radio>
          </Radio.Group>
        </Form.Item>
        {productionUnit !== "diger" ? (
          <>
            <Form.Item label="Şase Stok">
              <Select
                key={`chassis-stock-${stockSelectRenderKey}`}
                allowClear
                value={selectedChassisStock?.id ?? undefined}
                onChange={(value) => handleChassisStockChange(value)}
                placeholder="Şase stok seçin"
              >
                {filteredChassisStocks.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {`${item.quantity} Adet - ${item.production_unit.toUpperCase()} - ${item.brand} - ${item.type} - ${item.tank_size} LT`}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {productionUnit === "kollu" ? (
              <Form.Item label="Kol Stok">
                <Select
                  key={`arm-stock-${stockSelectRenderKey}`}
                  allowClear
                  value={selectedArmStock?.id ?? undefined}
                  onChange={(value) => handleArmStockChange(value)}
                  placeholder="Kol stok seçin"
                >
                  {filteredArmStocks.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {`${item.quantity} Adet - ${item.brand} - ${item.arm_type} - ${item.arm_length}`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            ) : null}
            <Form.Item label="Depo Stok">
              <Select
                key={`tank-stock-${stockSelectRenderKey}`}
                allowClear
                value={selectedTankStock?.id ?? undefined}
                onChange={(value) => handleTankStockChange(value)}
                placeholder="Depo stok seçin"
              >
                {filteredTankStocks.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {`${item.quantity} Adet - ${item.brand} - ${item.tank_size} LT - ${item.caldron_type}`}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </>
        ) : null}
        <Form.Item name="brand" label="Marka" required>
          <Select
            disabled={!!lockedFields.brand}
            onSelect={(type: any) => {
              const brand = configFile.brand.find(
                (brand) => brand.type == type
              );
              setSerialState({ ...serialState, brand: brand!.shortend });
            }}
          >
            {(function myFunction() {
              return configFile.brand.map((brand) => {
                return (
                  <Option key={brand.type} value={brand.type}>
                    {brand.text}
                  </Option>
                );
              });
            })()}
          </Select>
        </Form.Item>
        {productionUnit == "turbo" && serialState.brand == "T" ? (
          <Form.Item name="model" label="Model" required>
            <Select>
              {(function myFunction() {
                return configFile.turktraktormodel.map((brand) => {
                  return (
                    <Option key={brand.text} value={brand.text}>
                      {brand.text}
                    </Option>
                  );
                });
              })()}
            </Select>
          </Form.Item>
        ) : null}

        <Form.Item name="type" label="Tip" required>
          {productionUnit === "turbo" || productionUnit === "kollu" ? (
            <Select
              disabled={!!lockedFields.type}
              onSelect={(btype) => {
                const type = configFile.type.filter(
                  (type) => type.type == btype
                );
                setSerialState({ ...serialState, type: type[0].shortend });
              }}
            >
              {(function myFunction() {
                return configFile.type.map((type) => {
                  return (
                    <Option key={type.type} value={type.type}>
                      {type.text}
                    </Option>
                  );
                });
              })()}
            </Select>
          ) : (
            <Select
              disabled={!!lockedFields.type}
              onSelect={(btype: any) => {
                const type = configFile.machine_type.filter(
                  (machine_type) => machine_type.type == btype
                );
                setSerialState({ ...serialState, type: type[0].shortend });
              }}
            >
              {(function myFunction() {
                return configFile.machine_type.map((machine_type) => {
                  return (
                    <Option key={machine_type.type} value={machine_type.type}>
                      {machine_type.text}
                    </Option>
                  );
                });
              })()}
            </Select>
          )}
        </Form.Item>
        {productionUnit == "kollu" || productionUnit == "turbo" ? (
          <>
            <Form.Item
              name="caldron_color"
              rules={[
                { required: true, message: "Bu alan boş bırakılamaz" },
                { max: 32, message: "Maksimum sınıra ulaşıldı." },
              ]}
              label="Kazan Rengi"
              required
            >
              <Select disabled={!!lockedFields.caldron_color}>
                {caldronColorOptions.map((item) => (
                  <Option key={item.type} value={item.type}>
                    {item.text}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="caldron_stock" label="Kazan Tedarik">
              <Radio.Group disabled={!!selectedTankStock}>
                <Radio value="onstock">Stoktan</Radio>
                <Radio value="new">Kazan Üreticiden</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="caldron_material"
              rules={[
                { required: true, message: "Bu alan boş bırakılamaz" },
                { max: 19, message: "Maksimum sınıra ulaşıldı." },
              ]}
              label="Materyal"
              required
            >
              <Select>
                {(function myFunction() {
                  return configFile.caldron_material.map((caldron_material) => {
                    return (
                      <Option
                        key={caldron_material.type}
                        value={caldron_material.type}
                      >
                        {caldron_material.text}
                      </Option>
                    );
                  });
                })()}
              </Select>
            </Form.Item>
            <Form.Item name="tank_size" label="Depo Hacmi" required>
              <Select
                disabled={!!lockedFields.tank_size}
                onSelect={(type) => {
                  const tank_size = configFile.tank_size.filter(
                    (tank_size) => tank_size.type == type
                  );
                  setSerialState({
                    ...serialState,
                    tank_size: parseInt(tank_size[0].shortend),
                  });
                }}
              >
                <OptGroup label="Litre">
                  {(function myFunction() {
                    return configFile.tank_size.map((tank_size) => {
                      return (
                        <Option key={tank_size.type} value={tank_size.type}>
                          {tank_size.text}
                        </Option>
                      );
                    });
                  })()}
                </OptGroup>
              </Select>
            </Form.Item>
            <Form.Item name="tow_type" label="Çeki Tipi" required>
              <Select disabled={!!lockedFields.meme_type}>
                {(function myFunction() {
                  return configFile.tow_type.map((tow_type) => {
                    return (
                      <Option key={tow_type.type} value={tow_type.type}>
                        {tow_type.text}
                      </Option>
                    );
                  });
                })()}
              </Select>
            </Form.Item>
            <Form.Item name="pump" label="Pompa" required>
              <Select
                onSelect={(type: any) => {
                  const pump = configFile.pump.filter(
                    (pump) => pump.type == type
                  );
                  setSerialState({ ...serialState, pump: pump[0].shortend });
                }}
              >
                {(function myFunction() {
                  return configFile.pump.map((pump) => {
                    return (
                      <Option key={pump.type} value={pump.type}>
                        {pump.text}
                      </Option>
                    );
                  });
                })()}
              </Select>
            </Form.Item>
            <Form.Item name="commander" label="Kumanda" required>
              <Select>
                {(function myFunction() {
                  return configFile.commander.map((commander) => {
                    return (
                      <Option key={commander.type} value={commander.type}>
                        {commander.text}
                      </Option>
                    );
                  });
                })()}
              </Select>
            </Form.Item>
            <Form.Item
              name="meme_type"
              rules={[
                { required: true, message: "Bu alan boş bırakılamaz" },
                { max: 19, message: "Maksimum sınıra ulaşıldı." },
              ]}
              label="Meme Tipi"
              required
            >
              <Select>
                {(function myFunction() {
                  return configFile.meme_type.map((meme_type) => {
                    return (
                      <Option key={meme_type.type} value={meme_type.type}>
                        {meme_type.text}
                      </Option>
                    );
                  });
                })()}
              </Select>
            </Form.Item>

            <Form.Item name="mixer_type" label="Mixer Tipi">
              <Select>
                {(function myFunction() {
                  return configFile?.mixer_type?.map((mixer_type) => {
                    return (
                      <Option key={mixer_type.type} value={mixer_type.type}>
                        {mixer_type.text}
                      </Option>
                    );
                  });
                })()}
              </Select>
            </Form.Item>
          </>
        ) : null}
        {productionUnit == "kollu" ? (
          <>
            <Form.Item name="arm_type" label="Kol Tipi" required>
              <Select
                disabled={!!lockedFields.arm_type}
                onSelect={(btype: any) => {
                  const arm_type = configFile.arm_type.filter(
                    (type) => type.type == btype
                  );
                  setSerialState({
                    ...serialState,
                    arm_type: arm_type[0].shortend,
                  });
                }}
              >
                {(function myFunction() {
                  return configFile.arm_type.map((arm_type) => {
                    return (
                      <Option key={arm_type.type} value={arm_type.type}>
                        {arm_type.text}
                      </Option>
                    );
                  });
                })()}
              </Select>
            </Form.Item>

            <Form.Item
              name="meme_distance"
              rules={[
                { required: true, message: "Bu alan boş bırakılamaz" },
                { max: 19, message: "Maksimum sınıra ulaşıldı." },
              ]}
              label="Meme Aralığı"
              required
            >
              <Input maxLength={19} disabled={!!lockedFields.meme_distance} />
            </Form.Item>
            <Form.Item
              name="arm_length"
              rules={[
                { required: true, message: "Bu alan boş bırakılamaz" },
                { max: 30, message: "Maksimum sınıra ulaşıldı." },
              ]}
              label="Kol Uzunluğu"
              required
            >
              <Input maxLength={20} disabled={!!lockedFields.arm_length} />
            </Form.Item>
          </>
        ) : null}
        {productionUnit == "turbo" ? (
          <>
            <Form.Item name="cooler_type" label="Fan Tipi" required>
              <Select
                disabled={!!lockedFields.cooler_type}
                onSelect={(type: any) => {
                  const cooler_type = configFile.cooler_type.filter(
                    (cooler_type) => cooler_type.type == type
                  );
                  setSerialState({
                    ...serialState,
                    cooler_type: cooler_type[0].shortend,
                  });
                }}
              >
                {(function myFunction() {
                  return configFile.cooler_type.map((cooler_type) => {
                    return (
                      <Option key={cooler_type.type} value={cooler_type.type}>
                        {cooler_type.text}
                      </Option>
                    );
                  });
                })()}
              </Select>
            </Form.Item>

            {productionUnit == "turbo" ? (
              <Form.Item name="caldron_type" label="Kazan Tipi" required>
                <Select disabled={!!lockedFields.caldron_type}>
                  {(function myFunction() {
                    return configFile.caldron_type.map((caldron_type) => {
                      return (
                        <Option
                          key={caldron_type.type}
                          value={caldron_type.type}
                        >
                          {caldron_type.text}
                        </Option>
                      );
                    });
                  })()}
                </Select>
              </Form.Item>
            ) : null}

            {productionUnit == "turbo" ? (
              <Form.Item name="fan_radius" label="Pervane Çapı" required>
                <Select disabled={!!lockedFields.fan_type}>
                  {(function myFunction() {
                    return configFile.fan_radius.map((fan_radius) => {
                      return (
                        <Option key={fan_radius.type} value={fan_radius.type}>
                          {fan_radius.text}
                        </Option>
                      );
                    });
                  })()}
                </Select>
              </Form.Item>
            ) : null}
            {productionUnit == "turbo" ? (
              <Form.Item name="fan_type" label="Pervane Tipi" required>
                <Select>
                  {(function myFunction() {
                    return configFile.fan_type.map((fan_type) => {
                      return (
                        <Option key={fan_type.type} value={fan_type.type}>
                          {fan_type.text}
                        </Option>
                      );
                    });
                  })()}
                </Select>
              </Form.Item>
            ) : null}
          </>
        ) : null}

        <Form.Item name="order_no" label="Sipariş No" required>
          <Input disabled maxLength={6} />
        </Form.Item>
        <Form.Item
          name="order_amount"
          rules={[
            { required: true, message: "Bu alan boş bırakılamaz" },
            { max: 5, message: "Maksimum sınıra ulaşıldı." },
          ]}
          label="Sipariş Adedi"
          required
        >
          <Input
            max={99}
            min={1}
            type="number"
            onChange={(e) => {
              setSerialState({
                ...serialState,
                order_amount: parseInt(e.target.value),
              });
            }}
          />
        </Form.Item>
        <Form.Item name="year" label="Yıl" initialValue={moment()} required>
          <DatePicker disabled picker="year" />
        </Form.Item>

        <Form.Item name="chassis_color" label="Şase Rengi" required>
          <Select>
            {(function myFunction() {
              return configFile.chassis_color.map((chassis_color) => {
                return (
                  <Option key={chassis_color.type} value={chassis_color.type}>
                    {chassis_color.text}
                  </Option>
                );
              });
            })()}
          </Select>
        </Form.Item>

        <Form.Item
          name="alt_features"
          rules={[{ max: 250, message: "Maksimum sınıra ulaşıldı." }]}
          label="Ek Özellikler"
          required
        >
          <Input.TextArea maxLength={250} />
        </Form.Item>
        <Form.Item name="order_tooker" label="Sipariş Alan" required>
          <Select>
            {(function myFunction() {
              return configFile.order_tooker.map((order_tooker) => {
                return (
                  <Option key={order_tooker.type} value={order_tooker.type}>
                    {order_tooker.text}
                  </Option>
                );
              });
            })()}
          </Select>
        </Form.Item>
        <Form.Item label="Seri No">
          <Input disabled placeholder={serialCode} />
        </Form.Item>

        <Form.Item name="payment_type" label="Ödeme Tipi" required>
          <Radio.Group
            value={payment_type}
            defaultValue={payment_type}
            disabled
          >
            <Radio value="normal">Normal</Radio>
            <Radio value="ziraikredi">Zirai Kredi</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="payment_detail"
          rules={[{ max: 250, message: "Maksimum sınıra ulaşıldı." }]}
          label="Ödeme Detay"
          required
        >
          <Input.TextArea maxLength={250} />
        </Form.Item>

        <Form.Item required name="price" label="Fiyat">
          <Input type="number" />
        </Form.Item>

        <Form.Item wrapperCol={{ span: 12, offset: 6 }}>
          <Button
            disabled={isLoading}
            size="large"
            block
            type="primary"
            htmlType="submit"
          >
            {isLoading ? "Gönderiliyor.." : "Siparişi Düzenle"}
          </Button>
        </Form.Item>
      </Form>
      <div style={{ display: "block", width: "min-content" }}>
        <QRCode value={qrCodeValue} renderAs="svg" size={178} />
        {/*<Button type="primary" icon={<PrinterOutlined />} disabled={serialCode.length < 10} onClick={printQRCode} block>
          Yazdır
          </Button>*/}
      </div>
    </Body>
  );

  function SerialCodeGenerator(): string {
    switch (productionUnit) {
      case "kollu":
        return (
          serialState.brand +
          serialState.type +
          serialState.arm_type +
          serialState.tank_size +
          serialState.pump +
          0 +
          serialState.order_no +
          0 +
          serialState.order_amount
        );
      case "turbo":
        return (
          serialState.brand +
          serialState.type +
          serialState.cooler_type +
          serialState.tank_size +
          serialState.pump +
          0 +
          serialState.order_no +
          0 +
          serialState.order_amount
        );
      case "diger":
        return (
          serialState.brand +
          serialState.type +
          0 +
          serialState.order_no +
          0 +
          serialState.order_amount
        );
      default:
        return "";
    }
  }
}

export function splitTwoPartsBarcode(str: string) {
  var index = str.indexOf("0"); // Gets the first index where a space occours
  var amount = reverseString(str.substr(0, index)); // Gets the first part
  var serial = reverseString(str.substr(index + 1));

  return [serial, amount];
}

export function reverseString(str: string) {
  var newString = "";
  for (var i = str.length - 1; i >= 0; i--) {
    newString += str[i];
  }
  return newString;
}

export default EditOrderScreen;
